import { z } from "zod";

import {
  boundedTextSchema,
  entityIdSchema,
  hasUniqueValues,
  localOggAudioReferenceSchema,
  nonNegativeSafeIntegerSchema
} from "@/domain/schemas";

export const MIN_INSTRUCTION_STEPS = 1;
export const MAX_INSTRUCTION_STEPS = 6;

const MAX_TARGETS = 12;

export const instructionTargetDefinitionSchema = z.strictObject({
  id: entityIdSchema,
  label: boundedTextSchema(80)
});

export const instructionStepSchema = z.strictObject({
  id: entityIdSchema,
  order: nonNegativeSafeIntegerSchema,
  action: z.literal("select"),
  targetId: entityIdSchema,
  caption: boundedTextSchema(300),
  audio: localOggAudioReferenceSchema.optional()
});

export const instructionPayloadSchema = z
  .strictObject({
    targets: z.array(instructionTargetDefinitionSchema).min(1).max(MAX_TARGETS),
    steps: z
      .array(instructionStepSchema)
      .min(MIN_INSTRUCTION_STEPS)
      .max(MAX_INSTRUCTION_STEPS)
  })
  .superRefine((payload, context) => {
    const targetIds = payload.targets.map(({ id }) => id);
    const stepIds = payload.steps.map(({ id }) => id);
    const stepOrders = payload.steps.map(({ order }) => order);

    if (!hasUniqueValues(targetIds)) {
      context.addIssue({
        code: "custom",
        path: ["targets"],
        message: "Instruction target identifiers must be unique"
      });
    }

    if (!hasUniqueValues(stepIds)) {
      context.addIssue({
        code: "custom",
        path: ["steps"],
        message: "Instruction step identifiers must be unique"
      });
    }

    if (!hasUniqueValues(stepOrders)) {
      context.addIssue({
        code: "custom",
        path: ["steps"],
        message: "Instruction step order values must be unique"
      });
    }

    const knownTargetIds = new Set(targetIds);

    payload.steps.forEach((step, index) => {
      if (!knownTargetIds.has(step.targetId)) {
        context.addIssue({
          code: "custom",
          path: ["steps", index, "targetId"],
          message: "Instruction step references an unknown target"
        });
      }
    });
  });

export type InstructionTargetDefinition = z.infer<
  typeof instructionTargetDefinitionSchema
>;
export type InstructionStep = z.infer<typeof instructionStepSchema>;
export type InstructionPayload = z.infer<typeof instructionPayloadSchema>;

export function getOrderedInstructionSteps(
  payload: InstructionPayload
): readonly InstructionStep[] {
  return [...payload.steps].sort((left, right) => left.order - right.order);
}

export function getInstructionSequenceLength(
  payload: InstructionPayload
): number {
  return payload.steps.length;
}
